/**
 * Piece E — Social Post Queue / Status UI.
 *
 * Shows every social post (draft / scheduled / publishing / published /
 * partial / failed) with per-target, per-platform results and the EXACT
 * raw Meta Graph API error payload for anything that failed — so failures
 * can be diagnosed straight from the CRM without server access.
 *
 * Read-only over the publishing pipeline: publish / retry / cancel /
 * delete simply call the existing Piece C/D endpoints.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { socialAPI, clientGroupAPI } from '../lib/api';
import { sortClientsAlpha } from '../lib/sortClients';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { StellarSelect } from '../components/StellarFormControls';
import {
  Loader2, RefreshCw, Send, Trash2, ChevronDown, ChevronRight, Copy,
  CheckCircle2, XCircle, Clock3, CalendarClock, Instagram, Facebook,
  AlertTriangle, ExternalLink, ListChecks, CircleSlash,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatTaskDate } from '../lib/dateFormat';

const NONE = '__all__';

const STATUS_META = {
  draft:      { label: 'Draft',      cls: 'bg-foreground/10 text-foreground/70 border-border/40' },
  scheduled:  { label: 'Scheduled',  cls: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  publishing: { label: 'Publishing', cls: 'bg-gold/15 text-gold border-gold/40' },
  published:  { label: 'Published',  cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  partial:    { label: 'Partial',    cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  failed:     { label: 'Failed',     cls: 'bg-red-500/15 text-red-300 border-red-500/40' },
};
const STATUS_TABS = ['all', 'failed', 'partial', 'publishing', 'scheduled', 'draft', 'published'];

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${m.cls}`}
           data-testid={`queue-status-badge-${status}`}>
      {status === 'publishing' && <Loader2 className="h-2.5 w-2.5 mr-1 animate-spin" />}
      {m.label}
    </Badge>
  );
}

/** Raw Meta error disclosure — shows EVERY field Meta returned. */
function MetaErrorBox({ error, label }) {
  if (!error) return null;
  const rows = [
    ['Message', error.message],
    ['Type', error.type],
    ['Code', error.code != null ? String(error.code) : null],
    ['Subcode', error.error_subcode != null ? String(error.error_subcode) : null],
    ['User title', error.error_user_title],
    ['User message', error.error_user_msg],
    ['fbtrace_id', error.fbtrace_id],
    ['Endpoint', error.graph_url],
  ].filter(([, v]) => v);
  const copyAll = () => {
    try {
      navigator.clipboard.writeText(JSON.stringify(error, null, 2));
      toast.success('Error payload copied');
    } catch { toast.error('Copy failed'); }
  };
  return (
    <div className="rounded-md border border-red-500/30 bg-red-500/[0.06] p-2.5 mt-1.5"
         data-testid="queue-meta-error-box">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider text-red-300 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> {label || 'Meta API error'}
          {error.is_rate_limit && <Badge variant="outline" className="text-[9px] border-amber-500/50 text-amber-300 ml-1">rate limit</Badge>}
        </span>
        <button type="button" onClick={copyAll}
                className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                data-testid="queue-copy-error">
          <Copy className="h-3 w-3" /> Copy JSON
        </button>
      </div>
      <div className="space-y-0.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-2 text-[11px]">
            <span className="text-muted-foreground w-24 shrink-0">{k}</span>
            <span className="text-foreground/90 break-all">{String(v)}</span>
          </div>
        ))}
      </div>
      {error.graph_body_excerpt && (
        <pre className="mt-1.5 text-[10px] text-red-200/80 bg-black/30 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-40"
             data-testid="queue-error-raw-body">
          {error.graph_body_excerpt}
        </pre>
      )}
    </div>
  );
}

function PlatformResult({ platform, result }) {
  const Icon = platform === 'instagram' ? Instagram : Facebook;
  const tint = platform === 'instagram' ? 'text-pink-300' : 'text-[#4d94ff]';
  if (!result) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={`queue-platres-${platform}-pending`}>
        <Icon className={`h-3.5 w-3.5 ${tint}`} />
        <Clock3 className="h-3 w-3" /> Not attempted yet
      </div>
    );
  }
  if (result.ok) {
    return (
      <div className="flex items-center gap-1.5 text-[11px]" data-testid={`queue-platres-${platform}-ok`}>
        <Icon className={`h-3.5 w-3.5 ${tint}`} />
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
        <span className="text-emerald-300">Published</span>
        {result.published_id && <span className="text-muted-foreground">· id {result.published_id}</span>}
        {result.permalink && (
          <a href={result.permalink} target="_blank" rel="noreferrer"
             className="text-gold inline-flex items-center gap-0.5 hover:underline"
             data-testid={`queue-permalink-${platform}`}>
            View <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    );
  }
  return (
    <div data-testid={`queue-platres-${platform}-failed`}>
      <div className="flex items-center gap-1.5 text-[11px]">
        <Icon className={`h-3.5 w-3.5 ${tint}`} />
        <XCircle className="h-3.5 w-3.5 text-red-400" />
        <span className="text-red-300">Failed</span>
      </div>
      <MetaErrorBox error={result.error} label={`${platform === 'instagram' ? 'Instagram' : 'Facebook'} error`} />
    </div>
  );
}

function TargetRow({ target }) {
  const platforms = target.platforms || [];
  const results = target.results || {};
  const noResults = Object.keys(results).length === 0;
  return (
    <div className="rounded-md border border-border/30 bg-background/40 p-2.5 space-y-2"
         data-testid={`queue-target-${target.social_account_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="text-[12px] font-medium truncate block">{target.page_name || target.social_account_id}</span>
          <span className="text-[10px] text-muted-foreground">
            {platforms.join(' + ')}
            {target.ig_username ? ` · @${target.ig_username}` : ''}
            {target.attempts ? ` · ${target.attempts} attempt${target.attempts === 1 ? '' : 's'}` : ''}
          </span>
        </div>
        <StatusBadge status={target.status === 'pending' ? 'draft' : (target.status || 'draft')} />
      </div>
      {platforms.map((p) => (
        <PlatformResult key={p} platform={p} result={results[p]} />
      ))}
      {/* Fallback: target-level error when no per-platform results were recorded */}
      {noResults && target.error && <MetaErrorBox error={target.error} label="Publish error" />}
    </div>
  );
}

function PostCard({ post, clientName, expanded, onToggle, onAction, busyAction }) {
  const targets = post.targets || [];
  const failedCount = targets.filter((t) => t.status === 'failed').length;
  const publishedCount = targets.filter((t) => t.status === 'published').length;
  const st = post.status || 'draft';
  const canPublish = st === 'draft';
  const canRetry = ['failed', 'partial'].includes(st);
  const canCancel = st === 'scheduled';
  const canDelete = ['draft', 'failed', 'scheduled', 'partial'].includes(st);
  return (
    <Card data-testid={`queue-post-${post.id}`} className={st === 'failed' ? 'border-red-500/30' : ''}>
      <CardContent className="p-0">
        <button type="button" onClick={onToggle}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-foreground/[0.02] transition-colors"
                data-testid={`queue-post-toggle-${post.id}`}>
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={st} />
              <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{post.post_type || 'feed'}</Badge>
              {clientName && <span className="text-[11px] text-gold/80">{clientName}</span>}
              {failedCount > 0 && (
                <span className="text-[10px] text-red-300 inline-flex items-center gap-0.5" data-testid={`queue-failed-chip-${post.id}`}>
                  <XCircle className="h-3 w-3" /> {failedCount} failed
                </span>
              )}
            </div>
            <p className="text-[12px] text-foreground/85 truncate mt-1">
              {post.caption || <span className="italic text-muted-foreground">(no caption)</span>}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {targets.length} target{targets.length === 1 ? '' : 's'}
              {publishedCount > 0 ? ` · ${publishedCount} published` : ''}
              {post.scheduled_at ? ` · scheduled ${formatTaskDate(post.scheduled_at)}` : ''}
              {post.created_at ? ` · created ${formatTaskDate(post.created_at)}` : ''}
            </p>
          </div>
        </button>

        {expanded && (
          <div className="border-t border-border/30 p-3 space-y-2" data-testid={`queue-post-detail-${post.id}`}>
            {/* Post-level error (e.g. scheduler rejections) */}
            {post.error && <MetaErrorBox error={post.error} label="Post-level error" />}
            {targets.length === 0 ? (
              <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                <CircleSlash className="h-3.5 w-3.5" /> No publish targets on this post.
              </div>
            ) : targets.map((t) => <TargetRow key={t.social_account_id} target={t} />)}

            <div className="flex items-center gap-2 pt-1 flex-wrap">
              {canPublish && (
                <Button size="sm" className="h-7 text-[11px]" disabled={!!busyAction}
                        onClick={() => onAction('publish')} data-testid={`queue-publish-${post.id}`}>
                  {busyAction === 'publish' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                  Publish now
                </Button>
              )}
              {canRetry && (
                <Button size="sm" className="h-7 text-[11px]" disabled={!!busyAction}
                        onClick={() => onAction('retry')} data-testid={`queue-retry-${post.id}`}>
                  {busyAction === 'retry' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                  Retry failed
                </Button>
              )}
              {canCancel && (
                <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={!!busyAction}
                        onClick={() => onAction('cancel')} data-testid={`queue-cancel-${post.id}`}>
                  {busyAction === 'cancel' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CalendarClock className="h-3 w-3 mr-1" />}
                  Cancel schedule
                </Button>
              )}
              {canDelete && (
                <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-300 border-red-500/30 hover:bg-red-500/10"
                        disabled={!!busyAction}
                        onClick={() => onAction('delete')} data-testid={`queue-delete-${post.id}`}>
                  {busyAction === 'delete' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                  Delete
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SocialQueuePage() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.id;

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState('all');
  const [clientId, setClientId] = useState(NONE);
  const [platformFilter, setPlatformFilter] = useState(NONE);
  const [pageFilter, setPageFilter] = useState(NONE);
  const [clients, setClients] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({}); // {postId: actionName}
  const [scheduler, setScheduler] = useState(null);

  const clientNameById = useMemo(() => {
    const m = {};
    clients.forEach((c) => { if (c?.id) m[c.id] = c.name; });
    return m;
  }, [clients]);

  const fetchPosts = useCallback(async ({ silent = false } = {}) => {
    if (!wsId) return;
    if (!silent) setLoading(true);
    try {
      const r = await socialAPI.listPosts(wsId, {
        status: statusTab === 'all' ? undefined : statusTab,
        client_group_id: clientId === NONE ? undefined : clientId,
        limit: 100,
      });
      setPosts(r?.data?.items || []);
      setTotal(r?.data?.total || 0);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || 'Failed to load queue');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [wsId, statusTab, clientId]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  useEffect(() => {
    if (!wsId) return;
    clientGroupAPI.list(wsId).then((r) => setClients(sortClientsAlpha(r?.data || []))).catch(() => {});
    socialAPI.schedulerStatus().then((r) => setScheduler(r?.data || null)).catch(() => {});
  }, [wsId]);

  // Poll while anything is publishing so per-target statuses flip live.
  const anyPublishing = posts.some((p) => p.status === 'publishing');
  useEffect(() => {
    if (!anyPublishing) return undefined;
    const id = setInterval(() => fetchPosts({ silent: true }), 5000);
    return () => clearInterval(id);
  }, [anyPublishing, fetchPosts]);

  const runAction = async (post, action) => {
    setBusy((b) => ({ ...b, [post.id]: action }));
    try {
      if (action === 'publish') {
        await socialAPI.publishPost(wsId, post.id);
        toast.success('Publishing started');
      } else if (action === 'retry') {
        await socialAPI.retryPost(wsId, post.id);
        toast.success('Retrying failed targets');
      } else if (action === 'cancel') {
        await socialAPI.cancelSchedule(wsId, post.id);
        toast.success('Schedule cancelled — back to draft');
      } else if (action === 'delete') {
        await socialAPI.deletePost(wsId, post.id);
        toast.success('Post deleted');
      }
      await fetchPosts({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[post.id]; return n; });
    }
  };

  const clientOptions = useMemo(
    () => [{ value: NONE, label: 'All clients' }].concat(
      clients.map((c) => ({ value: c.id, label: c.name || 'Unnamed' })),
    ),
    [clients],
  );

  // Page/account filter options derived from the fetched posts' targets.
  const pageOptions = useMemo(() => {
    const seen = {};
    posts.forEach((p) => (p.targets || []).forEach((t) => {
      if (t.social_account_id && !seen[t.social_account_id]) {
        seen[t.social_account_id] = t.page_name || t.social_account_id;
      }
    }));
    return [{ value: NONE, label: 'All pages' }].concat(
      Object.entries(seen).map(([v, l]) => ({ value: v, label: l })).sort((a, b) => a.label.localeCompare(b.label)),
    );
  }, [posts]);

  const visiblePosts = useMemo(() => posts.filter((p) => {
    if (platformFilter !== NONE
        && !(p.targets || []).some((t) => (t.platforms || []).includes(platformFilter))) return false;
    if (pageFilter !== NONE
        && !(p.targets || []).some((t) => t.social_account_id === pageFilter)) return false;
    return true;
  }), [posts, platformFilter, pageFilter]);

  return (
    <div className="p-6 space-y-5 animate-fade-in-up" data-testid="social-queue-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <ListChecks className="h-6 w-6 text-gold" /> Post Queue
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every post's status per account and platform — including the exact Meta error for anything that failed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {scheduler && (
            <Badge variant="outline"
                   className={`text-[10px] ${scheduler.running ? 'border-emerald-500/40 text-emerald-300' : 'border-red-500/40 text-red-300'}`}
                   data-testid="queue-scheduler-chip">
              Scheduler {scheduler.running ? 'running' : 'stopped'} · every {scheduler.interval_seconds}s
            </Badge>
          )}
          <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => fetchPosts()}
                  data-testid="queue-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 flex-wrap" data-testid="queue-status-tabs">
          {STATUS_TABS.map((s) => (
            <button key={s} type="button" onClick={() => setStatusTab(s)}
                    className={`h-7 px-2.5 rounded-full text-[11px] border transition-colors ${
                      statusTab === s
                        ? 'bg-gold/15 border-gold/60 text-gold'
                        : 'border-border/40 text-muted-foreground hover:border-border'
                    }`}
                    data-testid={`queue-tab-${s}`}>
              {s === 'all' ? 'All' : (STATUS_META[s]?.label || s)}
            </button>
          ))}
        </div>
        <div className="w-48">
          <StellarSelect value={clientId} onValueChange={setClientId} options={clientOptions}
                         placeholder="All clients" testId="queue-client-filter" />
        </div>
        <div className="w-40">
          <StellarSelect value={platformFilter} onValueChange={setPlatformFilter}
                         options={[{ value: NONE, label: 'All platforms' },
                                   { value: 'facebook', label: 'Facebook' },
                                   { value: 'instagram', label: 'Instagram' }]}
                         placeholder="All platforms" testId="queue-platform-filter" />
        </div>
        <div className="w-48">
          <StellarSelect value={pageFilter} onValueChange={setPageFilter} options={pageOptions}
                         placeholder="All pages" testId="queue-page-filter" />
        </div>
        <span className="text-[11px] text-muted-foreground" data-testid="queue-total">
          {(platformFilter !== NONE || pageFilter !== NONE) ? `${visiblePosts.length} of ${total}` : total} post{total === 1 ? '' : 's'}
        </span>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16" data-testid="queue-loading">
          <Loader2 className="h-6 w-6 animate-spin text-gold/50" />
        </div>
      ) : visiblePosts.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm" data-testid="queue-empty">
          No posts{statusTab !== 'all' ? ` with status "${statusTab}"` : ''} match the current filters.
          Compose one on the <a href="/compose" className="text-gold underline">Post Composer</a>.
        </div>
      ) : (
        <div className="space-y-2">
          {visiblePosts.map((p) => (
            <PostCard key={p.id} post={p}
                      clientName={clientNameById[p.client_group_id]}
                      expanded={!!expanded[p.id]}
                      onToggle={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))}
                      onAction={(a) => runAction(p, a)}
                      busyAction={busy[p.id]} />
          ))}
        </div>
      )}
    </div>
  );
}
